"use client";

import { useLocale, useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import {
  DEPTH_ROOT_ID,
  QUIET_HOVER,
} from "@/components/creatorPortal/one/chrome";
import { Button } from "@/components/ui/design-system/button";
import { Sheet } from "@/components/ui/design-system/sheet";
import { Hint } from "@/components/ui/design-system/text";
import { cn } from "@/lib/utils";
import { AgreementText } from "./AgreementText";

/**
 * The agreement on a phone: the whole screen's height to read in, rather than
 * a box a third of it tall inside a scrolling page, which is two scrolls
 * nested and the inner one always losing to the thumb.
 *
 * Two ways out, both always drawn, so the footer never changes height as the
 * reading finishes. "Not now" leaves without agreeing and is live until the
 * end; "I've read it" is the finish and is live from the end. Exactly one is
 * ever the thing to press, and the line above them says which, so the sheet
 * never traps anyone and never offers two answers at once. Drag, backdrop and
 * the close button stand down until the end, since "Not now" is the exit and
 * a swipe that also leaves would make the choice by accident.
 */
export function AgreementSheet({
  open,
  onClose,
  text,
  reachedEnd,
  onReachedEnd,
}: {
  open: boolean;
  onClose: () => void;
  text: string;
  reachedEnd: boolean;
  onReachedEnd: () => void;
}) {
  const t = useTranslations("agreement");
  const locale = useLocale();
  const contentRef = useRef<HTMLDivElement>(null);

  const markIfAtEnd = (el: HTMLDivElement) => {
    // Same 8px of slack as the inline box: sub-pixel line heights and zoom
    // leave a residue that never quite reaches an exact equality.
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 8) onReachedEnd();
  };

  // A screen tall enough to show the whole agreement has nothing to scroll,
  // and waiting on a scroll that can never happen would hold the sheet shut.
  // A frame late, because the sheet lays out as it rises.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      if (contentRef.current) markIfAtEnd(contentRef.current);
    });
    return () => cancelAnimationFrame(frame);
  }, [open, text]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("name")}
      dismissible={reachedEnd}
      // The page sinks back as the sheet rises, as every portal sheet does:
      // this is a layer over the step, not a panel pasted onto it.
      depthRootId={DEPTH_ROOT_ID}
      contentRef={contentRef}
      onContentScroll={markIfAtEnd}
      // Portalled to the body, outside the `ds-next` the /join layout wears.
      className="ds-next"
      // Pinned, so the button is in view and visibly waiting the whole time
      // someone reads; the line above it says what it is waiting for, and
      // goes once there is nothing left to wait for.
      footer={
        <div className="flex flex-col items-center gap-nf-ds-2">
          <Hint muted className="pb-nf-ds-1 text-center">
            {reachedEnd ? t("sheet.canAgree") : t("sheet.readToEnd")}
          </Hint>
          <Button
            type="button"
            variant="ink"
            disabled={!reachedEnd}
            onClick={onClose}
            className="w-full"
          >
            {t("sheet.done")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={reachedEnd}
            onClick={onClose}
            className={cn("w-full", QUIET_HOVER)}
          >
            {t("sheet.notNow")}
          </Button>
        </div>
      }
    >
      {/* The agreement itself is counsel-approved English, translated only
          with counsel's sign-off, so a reader in another language is told
          before they start. */}
      {locale !== "en" ? (
        <Hint muted as="p" className="mb-nf-ds-4">
          {t("englishOnly")}
        </Hint>
      ) : null}
      <AgreementText text={text} />
    </Sheet>
  );
}
